import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { createJob, getCustomers, uploadJobFiles } from "../../lib/supabase/assignments";
import { createCustomer, findMatchingCustomer } from "../../lib/supabase/customers";
import { FiCheck, FiX, FiEdit3, FiHome } from "react-icons/fi";
import DragDropUploader from "../common/DragDropUploader";

import Toast from '../common/Toast';
import { BrickWall, Grid2X2 } from "lucide-react";
import { GiWoodBeam } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import { getEnabledExtraQuoteTypes, getQuoteLabel } from "../../lib/quoteTypes";



// ─── Quote type display config ────────────────────────────────────────────────
const DEPT_CONFIG = {
    roof: { label: 'Roof', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-400', icon: <FiHome className="w-4 h-4" /> },
    wall: { label: 'Wall', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-400', icon: <BrickWall className="w-4 h-4" /> },
    floor: { label: 'Floor', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-400', icon: <Grid2X2 className="w-4 h-4" /> },

    beams: { label: 'Beams', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400', icon: <GiWoodBeam className="w-4 h-4" /> },
    flooring_sheets: { label: 'Flooring Sheets', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-400', icon: <HiOutlineSquare3Stack3D className="w-4 h-4" /> },
};

// ─── Mirrors the DB trigger logic exactly ─────────────────────────────────────
const buildQuotePredictions = (uniqueId, quotes, jobInformation) => {
    const baseUid = uniqueId?.trim() || '';
    const units = jobInformation?.isUnit ? (parseInt(jobInformation?.unitCount) || 1) : 1;
    const storey = jobInformation?.isSingleStorey === false
        ? (jobInformation?.isTripleStorey ? 'triple' : 'double')
        : 'single';

    const selectedDepts = ['roof', 'wall', 'floor'].filter(d => quotes[d]?.isRequired);
    if (selectedDepts.length === 0) return [];

    const predictions = [];

    selectedDepts.forEach(dept => {
        for (let u = 1; u <= units; u++) {
            const suffix = units > 1 ? `-${u}` : '';

            if (dept === 'roof') {
                predictions.push({ dept, childId: `${baseUid}R${suffix}`, unit: u });
            }

            if (dept === 'floor') {
                predictions.push({ dept, childId: `${baseUid}F${suffix}`, unit: u });
                if (storey === 'triple') {
                    predictions.push({ dept, childId: `${baseUid}FL1${suffix}`, unit: u });
                }
            }

            if (dept === 'wall') {
                predictions.push({ dept, childId: `${baseUid}W${suffix}`, unit: u });
                if (storey === 'double' || storey === 'triple') {
                    predictions.push({ dept, childId: `${baseUid}WL1${suffix}`, unit: u });
                }
                if (storey === 'triple') {
                    predictions.push({ dept, childId: `${baseUid}WL2${suffix}`, unit: u });
                }
            }
        }
    });

    return predictions;
};

// ─── Quote Preview component ──────────────────────────────────────────────────
// NOTE: no useMemo here — handleNestedChange mutates nested objects in place so
// object references never change, making useMemo stale. Computing directly is
// correct and cheap enough.
const QuotePreview = ({ uniqueId, quotes, jobInformation, isTimberJob }) => {
    if (isTimberJob) return null;

    const hasAnyDept = ['roof', 'wall', 'floor'].some(d => quotes[d]?.isRequired);
    if (!hasAnyDept) return null;

    const predictions = buildQuotePredictions(uniqueId, quotes, jobInformation);

    const uid = uniqueId?.trim();
    const units = jobInformation?.isUnit ? (parseInt(jobInformation?.unitCount) || 1) : 1;

    // Group by unit for display
    const byUnit = [];
    for (let u = 1; u <= units; u++) {
        byUnit.push({ unit: u, items: predictions.filter(p => p.unit === u) });
    }

    return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 space-y-3">
            <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Departments to be created</span>
                <span className="ml-auto text-xs text-gray-400 font-medium">{predictions.length} total</span>
            </div>

            {!uid && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5">
                    Enter a Job ID above to see the exact child quote IDs
                </p>
            )}

            {byUnit.map(({ unit, items }) => (
                <div key={unit}>
                    {units > 1 && (
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                            Unit {unit}
                        </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {items.map((p, i) => {
                            const cfg = DEPT_CONFIG[p.dept];
                            return (
                                <span
                                    key={i}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold ${cfg.bg} ${cfg.text} ${cfg.border}`}
                                >
                                    {/* <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} /> */}
                                    {cfg.icon}
                                    {uid ? p.childId : (
                                        <span className="italic opacity-60">{p.childId || `${p.dept.charAt(0).toUpperCase()}…`}</span>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

const AdditionalSectionUploader = React.memo(({
    type,
    cfg,
    label,
    clearFiles,
    onFilesChange,
}) => {
    const handleFilesChange = useCallback((files) => {
        onFilesChange(type, files);
    }, [onFilesChange, type]);

    return (
        <div className={`p-4 rounded-lg border ${cfg.bg} ${cfg.border} space-y-3`}>
            <div className="flex items-center gap-2">
                {cfg.icon}
                <h3 className={`text-lg font-semibold ${cfg.text}`}>
                    {label}
                </h3>
            </div>

            <p className="text-sm text-gray-600">
                Upload files for {label}. This department does not use design, estimation, or unit splitting.
            </p>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
                <DragDropUploader
                    onFilesChange={handleFilesChange}
                    clearFiles={clearFiles}
                />
            </div>
        </div>
    );
});

const NewAssignment = () => {
    const { userProfile, company } = useAuth();
    const { companySlug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [buttonLoading, setButtonLoading] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isTimberJob, setIsTimberJob] = useState(false);

    const [showDefaultDateConfirm, setShowDefaultDateConfirm] = useState(false);
    const [pendingSubmitEvent, setPendingSubmitEvent] = useState(false);
    const [defaultDeptDates, setDefaultDeptDates] = useState({});

    const INITIAL_FORM_STATE = {
        customerName: "",
        customerNumber: "",
        customerMail: "",
        contactName: "",
        customerAddress: "",
        uniqueId: "",
        carpenterSupervisorName: "",
        carpenterSupervisorNumber: "",
        jobType: "Estimation",
        quotes: {
            roof: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "" },
            wall: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "" },
            floor: { isRequired: false, expectedFinishDate: "", expectedDeliveryDate: "" },
            timber: { isRequired: false, expectedDeliveryDate: "" },
            beams: { isRequired: false, files: [] },
            flooring_sheets: { isRequired: false, files: [] },
        },
        jobInformation: {
            roofType: "",
            roofTrussCentres: "",
            floors: [
                {
                    floorNumber: 1,
                    floorTrussSize: "",
                    flooring: "",
                    balconyIncluded: false
                }
            ],
            isSingleStorey: true,
            isTripleStorey: false,
            singleStorey: {
                loadBearingWallStudSizes: "90x45",
                nonLoadBearingWallStudSizes: "",
                nonLoadBearingWallStudCentres: "",
                wallHeight: ""
            },
            doubleStorey: {
                groundFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                },
                firstFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                }
            },
            tripleStorey: {
                groundFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                },
                firstFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                },
                secondFloor: {
                    loadBearingWallStudSizes: "90x45",
                    nonLoadBearingWallStudSizes: "",
                    nonLoadBearingWallStudCentres: "",
                    wallHeight: ""
                }
            },
            siteMeasure: {
                required: false,
                wall: false,
                floor: false,
                truss: false
            },
            isUnit: false,
            unitCount: "",
            notes: [],
            urgentQuote: false,
            urgentDesign: false
        },
        estimationFinishDate: "",
        pdfs: []
    };

    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [newNote, setNewNote] = useState("");
    const [editingNoteIndex, setEditingNoteIndex] = useState(null);
    const [editingNoteContent, setEditingNoteContent] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [clearPdfs, setClearPdfs] = useState(false);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const dropdownRef = useRef(null);
    const customerWrapperRef = useRef(null);
    const [showAddButton, setShowAddButton] = useState(false);

    const enabledExtraQuoteTypes = useMemo(() => {
        return getEnabledExtraQuoteTypes(company);
    }, [company?.settings]);

    const hasExtraQuoteTypes = enabledExtraQuoteTypes.length > 0;

    const hasMainSelectedSection =
        formData.quotes.roof.isRequired ||
        formData.quotes.wall.isRequired ||
        formData.quotes.floor.isRequired;

    const hasAdditionalSelectedSection = enabledExtraQuoteTypes.some(
        type => formData.quotes?.[type]?.isRequired
    );

    useEffect(() => {
        if (company?.id) {
            fetchCustomers();
        }
    }, [company]);

    const fetchCustomers = async () => {
        try {
            const customersData = await getCustomers(company.id);
            setCustomers(customersData);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (customerWrapperRef.current && !customerWrapperRef.current.contains(e.target)) {
                setDropdownVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const defaultEstimateDays = Number(company?.settings?.default_estimate_date || 0);

    const hasDefaultEstimateDate = defaultEstimateDays > 0;

    const toDateInputValue = (date) => {
        return date.toISOString().slice(0, 10);
    };

    const getDefaultEstimateDateValue = () => {
        const date = new Date();
        date.setDate(date.getDate() + defaultEstimateDays);
        return toDateInputValue(date);
    };

    const getSelectedDepartmentsForDefaultDates = () => {
        return Object.entries(formData.quotes)
            .filter(([, quote]) => quote?.isRequired)
            .map(([type]) => type);
    };

    const hasAnyMissingSelectedDeptDate = () => {
        return getSelectedDepartmentsForDefaultDates().some(type => {
            const q = formData.quotes?.[type];
            return !q?.expectedFinishDate && !q?.expectedDeliveryDate;
        });
    };

    const openDefaultDateConfirmation = () => {
        const defaultDate = getDefaultEstimateDateValue();

        const nextDates = {};
        getSelectedDepartmentsForDefaultDates().forEach(type => {
            const q = formData.quotes?.[type];

            nextDates[type] =
                q?.expectedFinishDate ||
                q?.expectedDeliveryDate ||
                defaultDate;
        });

        setDefaultDeptDates(nextDates);
        setShowDefaultDateConfirm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (buttonLoading) return;

        const nextFormData = {
            ...formData,
            quotes: {
                ...formData.quotes,
                timber: {
                    ...formData.quotes.timber,
                    isRequired: isTimberJob ? true : formData.quotes.timber.isRequired,
                },
                roof: {
                    ...formData.quotes.roof,
                    isRequired: isTimberJob ? false : formData.quotes.roof.isRequired,
                },
                wall: {
                    ...formData.quotes.wall,
                    isRequired: isTimberJob ? false : formData.quotes.wall.isRequired,
                },
                floor: {
                    ...formData.quotes.floor,
                    isRequired: isTimberJob ? false : formData.quotes.floor.isRequired,
                },
                beams: {
                    ...formData.quotes.beams,
                    isRequired: isTimberJob ? false : formData.quotes.beams.isRequired,
                },
                flooring_sheets: {
                    ...formData.quotes.flooring_sheets,
                    isRequired: isTimberJob ? false : formData.quotes.flooring_sheets.isRequired,
                },
            },
            jobType: isTimberJob ? "Timber" : formData.jobType,
        };

        const validationErrors = validateForm();
        setFormErrors(validationErrors);

        if (validationErrors.length > 0) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const selectedDepartments = Object.entries(nextFormData.quotes)
            .filter(([, quote]) => quote?.isRequired)
            .map(([type]) => type);

        const shouldShowDefaultDatePopup =
            hasDefaultEstimateDate &&
            selectedDepartments.some(type => {
                const q = nextFormData.quotes?.[type];
                return !q?.expectedFinishDate && !q?.expectedDeliveryDate;
            });

        if (shouldShowDefaultDatePopup) {
            const defaultDate = getDefaultEstimateDateValue();

            const nextDates = {};
            selectedDepartments.forEach(type => {
                const q = nextFormData.quotes?.[type];

                nextDates[type] =
                    q?.expectedFinishDate ||
                    q?.expectedDeliveryDate ||
                    defaultDate;
            });

            setDefaultDeptDates(nextDates);
            setShowDefaultDateConfirm(true);
            return;
        }

        await submitJob(null, nextFormData);
    };

    const submitJob = async (overrideDeptDates = null, baseFormData = formData) => {
        setButtonLoading(true);

        const toastId = Toast.info("Creating the assignment", {
            autoClose: false,
        });

        try {
            const payload = {
                ...baseFormData,
                quotes: Object.fromEntries(
                    Object.entries(baseFormData.quotes).map(([type, quote]) => {
                        const overrideDate = overrideDeptDates?.[type];

                        if (!overrideDate) return [type, quote];

                        return [
                            type,
                            {
                                ...quote,
                                expectedFinishDate: overrideDate,
                                expectedDeliveryDate: overrideDate,
                            },
                        ];
                    })
                ),
            };

            const { job } = await createJob(
                payload,
                company.id,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                company.slug
            );

            if (payload.pdfs.length > 0) {
                await uploadJobFiles(
                    job.id,
                    payload.pdfs,
                    userProfile.id,
                    userProfile.username || userProfile.full_name,
                    companySlug || company.slug
                );
            }

            // Save the customer unless one with the same name, email and phone
            // already exists. Checked even when picked from the dropdown, since
            // the phone/email may have been edited afterwards.
            if (payload.customerName?.trim()) {
                try {
                    const customerFields = {
                        name: payload.customerName.trim(),
                        phone: payload.customerNumber ? `+61${payload.customerNumber}` : null,
                        email: payload.customerMail?.trim() || null,
                    };

                    const existing = await findMatchingCustomer(company.id, customerFields);

                    if (!existing) {
                        const newCust = await createCustomer({
                            company_id: company.id,
                            ...customerFields,
                            contact_name: payload.contactName?.trim() || null,
                            created_by: userProfile.id,
                        });

                        setCustomers(prev =>
                            [...prev, newCust].sort((a, b) => a.name.localeCompare(b.name))
                        );
                    }
                } catch (custErr) {
                    console.warn("[NewAssignment] Customer save skipped:", custErr?.message);
                }
            }

            setFormData({ ...INITIAL_FORM_STATE });
            setIsTimberJob(false);
            setInputValue("");
            setFormErrors([]);
            setDefaultDeptDates({});
            setShowDefaultDateConfirm(false);
            setClearPdfs(true);
            setTimeout(() => setClearPdfs(false), 1000);

            const requiredQuotes = Object.entries(payload.quotes)
                .filter(([, quote]) => quote.isRequired === true);

            Toast.update(
                toastId,
                <div className="w-full !text-zinc-900">
                    <h3 className="text-blue-600">
                        Job Created <span className="mx-1 text-gray-400">|</span>{" "}
                        <span className="font-semibold">#{payload.uniqueId}</span>
                    </h3>

                    <p className="flex flex-row capitalize gap-2 text-sm">
                        {requiredQuotes.map(([quoteKey]) => (
                            <span key={quoteKey} className="font-semibold">
                                {getQuoteLabel(quoteKey)}
                            </span>
                        ))}
                    </p>

                    <div className="flex flex-row gap-2 w-full h-10 mt-2">
                        <button
                            className="text-sm font-bold w-full border border-gray-400 rounded hover:border-blue-600 hover:text-blue-600 transition-all"
                            onClick={() =>
                                navigate(`/${company.slug}/assignment/list`, {
                                    state: { from: location.pathname },
                                })
                            }
                        >
                            Job List
                        </button>

                        <button
                            className="text-sm font-bold w-full border border-gray-400 rounded hover:border-blue-600 hover:text-blue-600 transition-all"
                            onClick={() =>
                                navigate(`/${company.slug}/assignment/${job.id}`, {
                                    state: { from: location.pathname },
                                })
                            }
                        >
                            See Job
                        </button>
                    </div>
                </div>,
                {
                    position: "bottom-right",
                    autoClose: 15000,
                    className: "w-full",
                }
            );
        } catch (error) {
            console.error("Error creating job:", error);

            let errorMessage = "Job creation failed, please try again.";
            if (error?.code === "23505") {
                errorMessage = "Job Id exist, please rename the job and try again.";
            }

            Toast.error(errorMessage, {
                title: "Error Creating Job",
            });
        } finally {
            setButtonLoading(false);
        }
    };

    const validateForm = () => {
        const errors = [];

        // Check if custom ID is enabled
        if (!formData.uniqueId?.trim()) {
            errors.push("Job ID is required");
        }

        if (!formData.customerName?.trim()) {
            errors.push("Customer name is required");
        }

        // if (!formData.customerNumber?.trim()) {
        //     errors.push("Customer phone number is required");
        // }

        if (!formData.customerMail?.trim()) {
            errors.push("Customer email address is required");
        }

        if (!formData.contactName?.trim()) {
            errors.push("Contact name is required");
        }

        if (isTimberJob) {
            return errors;
        }

        if (!formData.jobType) {
            errors.push("Job type is required");
        }

        const hasSelectedSection = hasMainSelectedSection || hasAdditionalSelectedSection;

        if (!hasSelectedSection) {
            errors.push("At least one section must be selected");
        }

        // enabledExtraQuoteTypes.forEach(type => {
        //     if (formData.quotes?.[type]?.isRequired && !formData.quotes?.[type]?.files?.length) {
        //         errors.push(`${getQuoteLabel(type)} requires at least one file`);
        //     }
        // });

        return errors;
    };

    // Add this component before the main return
    const ErrorDisplay = ({ errors }) => {
        if (errors.length === 0) return null;

        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="text-red-800 font-medium mb-2">Please fix the following errors:</h3>
                <ul className="text-red-700 text-sm space-y-1">
                    {errors.map((error, index) => (
                        <li key={index} className="flex items-start">
                            <span className="text-red-500 mr-2">•</span>
                            {error}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const addNote = () => {
        if (!newNote.trim()) return;

        const noteObject = {
            content: newNote.trim(),
            addedBy: {
                userId: "temp-admin", // Will be set by backend
                name: "Admin",
                role: "admin"
            },
            addedAt: new Date().toISOString(),
            isEdited: false
        };

        setFormData(prevState => ({
            ...prevState,
            jobInformation: {
                ...prevState.jobInformation,
                notes: [...prevState.jobInformation.notes, noteObject]
            }
        }));

        setNewNote("");
    };

    const startEditNote = (index, content) => {
        setEditingNoteIndex(index);
        setEditingNoteContent(content);
    };

    const saveEditNote = (index) => {
        if (!editingNoteContent.trim()) return;

        const updatedNotes = [...formData.jobInformation.notes];
        updatedNotes[index] = {
            ...updatedNotes[index],
            content: editingNoteContent.trim(),
            isEdited: true,
            editedAt: new Date().toISOString()
        };

        setFormData(prevState => ({
            ...prevState,
            jobInformation: {
                ...prevState.jobInformation,
                notes: updatedNotes
            }
        }));

        cancelEditNote();
    };

    const cancelEditNote = () => {
        setEditingNoteIndex(null);
        setEditingNoteContent("");
    };

    const deleteNote = (index) => {
        if (window.confirm("Are you sure you want to delete this note?")) {
            const updatedNotes = formData.jobInformation.notes.filter((_, i) => i !== index);
            setFormData(prevState => ({
                ...prevState,
                jobInformation: {
                    ...prevState.jobInformation,
                    notes: updatedNotes
                }
            }));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    // Custom button component for selections
    const SelectionButton = ({ label, isSelected, onClick, className = "" }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium ${isSelected
                ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                } ${className}`}
        >
            {label}
        </button>
    );

    // Custom checkbox button component
    const CheckboxButton = ({ label, isChecked, onClick, className = "" }) => (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium flex items-center gap-2 ${isChecked
                ? 'bg-green-500 text-white border-green-500 shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:border-green-300 hover:bg-green-50'
                } ${className}`}
        >
            {isChecked ? <FiCheck className="w-4 h-4" /> : <FiX className="w-4 h-4" />}
            {label}
        </button>
    );

    const renderWallSpecs = (prefix, data, title) => (
        <div className="bg-red-100/70 p-4 rounded-lg space-y-4 mt-4">
            <h4 className="text-lg font-semibold text-gray-800">{title}</h4>

            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Non Load Bearing Wall Stud Sizes</p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="90x45"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x45"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudSizes`, "90x45")}
                    />
                    <SelectionButton
                        label="90x35"
                        isSelected={data.nonLoadBearingWallStudSizes === "90x35"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudSizes`, "90x35")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Non Load Bearing Wall Stud Centres</p>
                <div className="flex gap-2 sm:flex-row flex-col">
                    <SelectionButton
                        label="450"
                        isSelected={data.nonLoadBearingWallStudCentres === "450"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudCentres`, "450")}
                    />
                    <SelectionButton
                        label="600"
                        isSelected={data.nonLoadBearingWallStudCentres === "600"}
                        onClick={() => handleToggleSelection(`jobInformation.${prefix}.nonLoadBearingWallStudCentres`, "600")}
                    />
                </div>
            </div>

            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Wall Height</p>
                <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    type="text"
                    value={data.wallHeight}
                    onChange={(e) => handleNestedChange(`jobInformation.${prefix}.wallHeight`, e.target.value)}
                    placeholder="Wall Height (general without stepdown)"
                />
            </div>
        </div>
    );

    const normaliseAuPhone = (value) => {
        if (!value) return "";

        let digits = String(value).replace(/\D/g, "");

        // Convert 0061XXXXXXXXX → 61XXXXXXXXX
        if (digits.startsWith("0061")) {
            digits = digits.slice(2);
        }

        // Convert +61XXXXXXXXX / 61XXXXXXXXX → local mobile
        if (digits.startsWith("61")) {
            digits = digits.slice(2);
        }

        // Convert 04XXXXXXXX → 4XXXXXXXX
        if (digits.startsWith("0")) {
            digits = digits.slice(1);
        }

        // Keep only first 9 digits
        return digits.slice(0, 9);
    };

    const formatPhoneDisplay = (value) => {
        const d = normaliseAuPhone(value);

        if (d.length <= 3) return d;
        if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
        return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            handleNestedChange(name, type === 'checkbox' ? checked : value);
            return;
        }

        const filtered = customers.filter((customer) =>
            customer.name.toLowerCase().includes(value.toLowerCase())
        );

        if (name === "customerNumber") {
            const phone = normaliseAuPhone(value);

            setFormData(prevState => ({
                ...prevState,
                customerNumber: phone
            }));
        } else if (name === "carpenterSupervisorNumber") {
            const phone = normaliseAuPhone(value);

            setFormData(prevState => ({
                ...prevState,
                carpenterSupervisorNumber: phone
            }));
        } else if (name === "customerName") {
            setInputValue(value);
            setFilteredCustomers(filtered);
            setDropdownVisible(value.length > 0);
            setFormData(prevState => ({ ...prevState, customerName: value }));
        } else {
            setFormData(prevState => ({ ...prevState, [name]: type === 'checkbox' ? checked : value }));
        }

        setShowAddButton(filtered.length === 0 && value.trim() !== "" && formData.customerNumber.trim() !== "");
    };

    const handleNestedChange = (path, value) => {
        const keys = path.split('.');
        setFormData(prevState => {
            const newState = { ...prevState };
            let current = newState;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newState;
        });
    };

    // Helper function to toggle selection - if already selected, reset to empty/initial value
    const handleToggleSelection = (path, value, initialValue = "") => {
        const keys = path.split('.');
        let current = formData;

        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }

        const currentValue = current[keys[keys.length - 1]];
        const newValue = currentValue === value ? initialValue : value;

        handleNestedChange(path, newValue);
    };

    const handleCustomerSelect = (customer) => {
        // Strip +61 prefix from stored phone if present
        const rawPhone = normaliseAuPhone(customer.phone || '');
        setFormData(prevState => ({
            ...prevState,
            customerName: customer.name,
            customerNumber: rawPhone,
            customerMail: customer.email || '',
            contactName: customer.contact_name || '',
        }));

        setFilteredCustomers([]);
        setInputValue(customer.name);
        setDropdownVisible(false);
        setShowAddButton(false);
    };

    const handleInputClick = () => {
        if (inputValue.length > 0) {
            setDropdownVisible(true);
        }
    };

    const handleAddCustomer = () => {
        // const token = Cookies.get("token");
        // if (!token) {
        //     navigate("/login");
        // } else {
        //     axios
        //         .post(
        //             `${process.env[`REACT_APP_R_BASE_SERVER_URL_${company.toUpperCase()}`]}/admin/add-customer`,
        //             {
        //                 name: formData.customerName,
        //                 phone: `+61${formData.customerNumber}`,
        //             },
        //             {
        //                 headers: { Authorization: `Bearer ${token}` },
        //             }
        //         )
        //         .then((response) => {
        //             alert(response.data.message);
        //             setCustomers([...customers, response.data.customer]);
        //             setDropdownVisible(false);
        //             setShowAddButton(false);
        //         })
        //         .catch((error) => {
        //             console.error("Error adding customer:", error);
        //             alert("Error adding customer.");
        //         });
        // }
    };

    const handlePdfsChange = useCallback((pdfFiles) => {
        setFormData(prevState => ({ ...prevState, pdfs: pdfFiles }));
    }, []);

    const handleAdditionalSectionFilesChange = useCallback((quoteType, files) => {
        setFormData(prevState => ({
            ...prevState,
            quotes: {
                ...prevState.quotes,
                [quoteType]: {
                    ...prevState.quotes[quoteType],
                    files
                }
            }
        }));
    }, []);



    return (
        <>
            <form onSubmit={handleSubmit} className="new-assignment-form space-y-8">
                <div className="text-start">
                    <h1 className="sm:text-3xl text-2xl font-bold text-gray-900 mb-2">{isTimberJob ? 'New Timber Job' : 'New Construction Job'}</h1>
                    <p className="text-gray-600">{isTimberJob ? 'Create a new timber job assignment' : 'Create a new construction job assignment'}</p>
                </div>

                <div className="new-job-category hidden" role="group" aria-label="Job category">
                    <button type="button" aria-pressed={!isTimberJob} onClick={() => setIsTimberJob(false)}>Construction</button>
                    <button type="button" aria-pressed={isTimberJob} onClick={() => setIsTimberJob(true)}>Timber</button>
                </div>
                {/* Job Category Toggle */}
                <div className="new-job-category-desktop space-y-3">
                    <div className="flex items-start justify-start">
                        <button
                            type="button"
                            onClick={() => setIsTimberJob(!isTimberJob)}
                            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${isTimberJob
                                ? 'bg-amber-500 text-white hover:bg-amber-600'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                }`}
                        >
                            {isTimberJob ? 'Timber Job' : 'Construction Job'}
                        </button>
                    </div>
                    <p className="text-start text-sm text-gray-500">
                        {isTimberJob ? 'Click to switch to Construction Job' : 'Click to switch to Timber Job'}
                    </p>
                </div>

                {/* Job ID */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">Job Identification</h2>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Job ID / Unique Identifier</label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            name="uniqueId"
                            value={formData.uniqueId}
                            onChange={handleChange}
                            placeholder="Enter unique job identifier"
                            required
                        />
                    </div>
                </div>

                {/* Customer Information */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Customer Information</h2>

                    <div className="relative" ref={customerWrapperRef}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Customer Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            value={inputValue}
                            onChange={handleChange}
                            onClick={handleInputClick}
                            name="customerName"
                            placeholder="Type to search existing customers…"
                            autoComplete="off"
                            required
                        />
                        {dropdownVisible && filteredCustomers.length > 0 && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-56 overflow-y-auto" ref={dropdownRef}>
                                {filteredCustomers.slice(0, 8).map((customer) => (
                                    <button
                                        key={customer.id}
                                        type="button"
                                        className="w-full px-4 py-2.5 text-left hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg flex flex-col gap-0.5 border-b border-gray-100 last:border-b-0"
                                        onClick={() => handleCustomerSelect(customer)}
                                    >
                                        <span className="font-medium text-gray-900 text-sm">{customer.name}</span>
                                        {(customer.phone || customer.email) && (
                                            <span className="text-xs text-gray-500">
                                                {[customer.phone, customer.email].filter(Boolean).join(' · ')}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                        {dropdownVisible && filteredCustomers.length === 0 && inputValue.trim() && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg" ref={dropdownRef}>
                                <p className="px-4 py-2.5 text-sm text-gray-500 italic">No matching customers — will be saved as new</p>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Customer Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="flex">
                            <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                +61
                            </span>
                            <input
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                                type="tel"
                                inputMode="tel"
                                autoComplete="tel"
                                name="customerNumber"
                                value={formatPhoneDisplay(formData.customerNumber)}
                                onChange={handleChange}
                                placeholder="XXX XXX XXX"
                                maxLength={20}
                            //required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Customer Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="email"
                            name="customerMail"
                            value={formData.customerMail}
                            onChange={handleChange}
                            placeholder="Customer email address"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleChange}
                            placeholder="Contact person name"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Job Address</label>

                        <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            type="text"
                            name="customerAddress"
                            value={formData.customerAddress}
                            onChange={handleChange}
                            placeholder="Job address"
                        />

                    </div>

                    {/* {showAddButton && (
                        <button
                            type="button"
                            onClick={handleAddCustomer}
                            className="w-full py-2 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                            Add to Customers
                        </button>
                    )} */}
                </div>

                {/* Timber Job Expected Delivery Date */}
                {isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-orange-500 pb-2">Delivery Information</h2>
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                            <div className="relative">
                                <input
                                    className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    type="date"
                                    value={formData?.quotes?.timber?.expectedDeliveryDate || ""}
                                    onChange={(e) => handleNestedChange("quotes.timber.expectedDeliveryDate", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Supervisor Information */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Carpenter <span className="text-sm text-black/40 mx-1">or</span> Supervisor</h2>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                            <input
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                type="text"
                                name="carpenterSupervisorName"
                                value={formData.carpenterSupervisorName}
                                onChange={handleChange}
                                placeholder="Enter supervisor name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                            <div className="flex">
                                <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                                    +61
                                </span>
                                <input
                                    className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    name="carpenterSupervisorNumber"
                                    value={formatPhoneDisplay(formData.carpenterSupervisorNumber)}
                                    onChange={handleChange}
                                    placeholder="XXX XXX XXX"
                                    maxLength={20}
                                />
                            </div>
                        </div>
                    </div>
                )}


                {/* Job Type */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Job Type</h2>
                        <div className="sm:flex-row flex gap-3 flex-col">
                            <SelectionButton
                                label="Design Only"
                                isSelected={formData.jobType === "Design"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Design" }))}
                            />
                            <SelectionButton
                                label="Estimation Only"
                                isSelected={formData.jobType === "Estimation"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Estimation" }))}
                            />
                            <SelectionButton
                                label="Both Design & Estimation"
                                isSelected={formData.jobType === "Design & Estimation"}
                                onClick={() => setFormData(prev => ({ ...prev, jobType: "Design & Estimation" }))}
                            />
                        </div>
                    </div>
                )}

                {!isTimberJob && (
                    <>
                        {/* Quote Selection */}
                        < div className="space-y-4">
                            <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Required Sections</h2>
                            <p className="text-sm text-gray-600">Select which sections are needed for this job</p>


                            {!formData.quotes.roof.isRequired && !formData.quotes.wall.isRequired && !formData.quotes.floor.isRequired && (
                                <p className="text-red-500 text-sm">⚠️ Please select at least one section</p>
                            )}


                            <div className="new-required-sections flex gap-3 flex-wrap flex-col sm:flex-row">
                                <CheckboxButton
                                    label="Roof"
                                    isChecked={formData.quotes.roof.isRequired}
                                    onClick={() => handleNestedChange('quotes.roof.isRequired', !formData.quotes.roof.isRequired)}
                                />
                                <div>
                                    <CheckboxButton
                                        label="Wall"
                                        isChecked={formData.quotes.wall.isRequired}
                                        onClick={() => handleNestedChange('quotes.wall.isRequired', !formData.quotes.wall.isRequired)}
                                    />
                                </div>
                                <div>
                                    <CheckboxButton
                                        label="Floor"
                                        isChecked={formData.quotes.floor.isRequired}
                                        onClick={() => {
                                            // if (!formData.quotes.floor.isRequired && formData.jobInformation.isSingleStorey === true) return;
                                            handleNestedChange('quotes.floor.isRequired', !formData.quotes.floor.isRequired);
                                        }}
                                    />
                                    {/* {formData.jobInformation.isSingleStorey === true && (
                                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                            ⚠ Single Storey and Floor cannot be selected together
                                        </p>
                                    )} */}
                                </div>
                            </div>

                            {hasExtraQuoteTypes && (
                                <div className="pt-5 mt-5 border-t border-gray-200 space-y-3">
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-800">Additional Sections</h3>
                                        <p className="text-xs text-gray-500">
                                            These sections only require files. Unit count does not create multiple departments.
                                        </p>
                                    </div>

                                    <div className="new-required-sections flex gap-3 flex-wrap flex-col sm:flex-row">
                                        {enabledExtraQuoteTypes.map(type => (
                                            <CheckboxButton
                                                key={type}
                                                label={getQuoteLabel(type)}
                                                isChecked={formData.quotes?.[type]?.isRequired}
                                                onClick={() => handleNestedChange(
                                                    `quotes.${type}.isRequired`,
                                                    !formData.quotes?.[type]?.isRequired
                                                )}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {!isTimberJob && hasExtraQuoteTypes && hasAdditionalSelectedSection && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">
                            Additional Section Files
                        </h2>

                        {enabledExtraQuoteTypes
                            .filter(type => formData.quotes?.[type]?.isRequired)
                            .map(type => {
                                const cfg = DEPT_CONFIG[type];

                                return (
                                    <AdditionalSectionUploader
                                        key={type}
                                        type={type}
                                        cfg={cfg}
                                        label={getQuoteLabel(type)}
                                        clearFiles={clearPdfs}
                                        onFilesChange={handleAdditionalSectionFilesChange}
                                    />
                                );
                            })}
                    </div>
                )}

                {/* Job Specifications */}
                {!isTimberJob && (
                    <>
                        {hasMainSelectedSection && (
                            <div className="space-y-6">
                                <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Job Specifications</h2>

                                {/* Roof Specifications */}
                                {formData.quotes.roof.isRequired && (
                                    <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Roof Specifications</h3>

                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Roof Type</p>
                                            <div className="flex gap-2 sm:flex-row flex-col">
                                                <SelectionButton
                                                    label="Tiled"
                                                    isSelected={formData.jobInformation.roofType === "Tiled"}
                                                    onClick={() => {
                                                        if (formData.jobInformation.roofType === "Tiled") {
                                                            handleNestedChange('jobInformation.roofType', "");
                                                            handleNestedChange('jobInformation.roofTrussCentres', "");
                                                        } else {
                                                            handleNestedChange('jobInformation.roofType', "Tiled");
                                                            handleNestedChange('jobInformation.roofTrussCentres', "600");
                                                        }
                                                    }}
                                                />
                                                <SelectionButton
                                                    label="Colorbond"
                                                    isSelected={formData.jobInformation.roofType === "Colorbond"}
                                                    onClick={() => handleToggleSelection('jobInformation.roofType', "Colorbond")}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Roof Truss Centres</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <SelectionButton
                                                    label="600"
                                                    isSelected={formData.jobInformation.roofTrussCentres === "600"}
                                                    onClick={() => handleToggleSelection('jobInformation.roofTrussCentres', "600")}
                                                />
                                                {formData?.jobInformation?.roofType !== "Tiled" &&
                                                    <SelectionButton
                                                        label="900"
                                                        isSelected={formData.jobInformation.roofTrussCentres === "900"}
                                                        onClick={() => handleToggleSelection('jobInformation.roofTrussCentres', "900")}
                                                    />
                                                }
                                            </div>
                                        </div>

                                        {/* Estimation Date Roof */}
                                        {/* <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <div className="relative w-full">
                                                    <input
                                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        type="date"
                                                        name="estimationFinishDate"
                                                        value={formData?.quotes?.roof?.expectedFinishDate || ""}
                                                        onChange={(e) =>
                                                            handleNestedChange("quotes.roof.expectedFinishDate", e.target.value)
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Expected Delivery Date Roof */}
                                        {formData.jobType !== "Estimation" &&
                                            <div>
                                                <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                                <div className="flex gap-2 flex-wrap">
                                                    <div className="relative w-full">
                                                        <input
                                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            type="date"
                                                            value={formData?.quotes?.roof?.expectedDeliveryDate || ""}
                                                            onChange={(e) => handleNestedChange("quotes.roof.expectedDeliveryDate", e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        }

                                    </div>
                                )}


                                {/* Wall Specifications */}
                                {(formData.quotes.wall.isRequired) && (
                                    <div className="bg-red-50 p-4 rounded-lg space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Wall Specifications</h3>

                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Building Type</p>
                                            <div className="flex gap-2 sm:flex-row flex-col flex-wrap">
                                                <SelectionButton
                                                    label="Single Storey"
                                                    isSelected={formData.jobInformation.isSingleStorey === true}
                                                    onClick={() => {
                                                        handleNestedChange('jobInformation.isSingleStorey', formData.jobInformation.isSingleStorey === true ? null : true);
                                                        handleNestedChange('jobInformation.isTripleStorey', false);
                                                        // if (formData.jobInformation.isSingleStorey !== true) {
                                                        //     handleNestedChange('quotes.floor.isRequired', false);
                                                        // }
                                                    }}
                                                />
                                                <SelectionButton
                                                    label="Double Storey"
                                                    isSelected={formData.jobInformation.isSingleStorey === false && !formData.jobInformation.isTripleStorey}
                                                    onClick={() => {
                                                        handleNestedChange('jobInformation.isSingleStorey', formData.jobInformation.isSingleStorey === false && !formData.jobInformation.isTripleStorey ? null : false);
                                                        handleNestedChange('jobInformation.isTripleStorey', false);
                                                    }}
                                                />
                                                <SelectionButton
                                                    label="Triple Storey"
                                                    isSelected={formData.jobInformation.isSingleStorey === false && formData.jobInformation.isTripleStorey === true}
                                                    onClick={() => {
                                                        handleNestedChange('jobInformation.isSingleStorey', false);
                                                        handleNestedChange('jobInformation.isTripleStorey', !formData.jobInformation.isTripleStorey || formData.jobInformation.isSingleStorey !== false ? true : false);
                                                    }}
                                                />
                                            </div>
                                            {formData.jobInformation.isSingleStorey === true ? (
                                                renderWallSpecs('singleStorey', formData.jobInformation.singleStorey, 'Single Storey Specifications')
                                            ) : formData.jobInformation.isSingleStorey === false && formData.jobInformation.isTripleStorey ? (
                                                <div className="space-y-4 mt-2">
                                                    {renderWallSpecs('tripleStorey.groundFloor', formData.jobInformation.tripleStorey.groundFloor, 'Ground Floor Specifications')}
                                                    {renderWallSpecs('tripleStorey.firstFloor', formData.jobInformation.tripleStorey.firstFloor, 'First Floor Specifications')}
                                                    {renderWallSpecs('tripleStorey.secondFloor', formData.jobInformation.tripleStorey.secondFloor, 'Second Floor Specifications')}
                                                </div>
                                            ) : formData.jobInformation.isSingleStorey === false ? (
                                                <div className="space-y-4 mt-2">
                                                    {renderWallSpecs('doubleStorey.groundFloor', formData.jobInformation.doubleStorey.groundFloor, 'Ground Floor Specifications')}
                                                    {renderWallSpecs('doubleStorey.firstFloor', formData.jobInformation.doubleStorey.firstFloor, 'First Floor Specifications')}
                                                </div>
                                            ) : null}
                                        </div>

                                        {/* Estimation Date Wall */}
                                        {/* <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <div className="relative w-full">
                                                    <input
                                                        className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        type="date"
                                                        name="estimationFinishDate"
                                                        value={formData?.quotes?.wall?.expectedFinishDate || ""}
                                                        onChange={(e) => handleNestedChange("quotes.wall.expectedFinishDate", e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Expected Delivery Date Wall */}
                                        {formData.jobType !== "Estimation" &&
                                            <div>
                                                <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                                <div className="flex gap-2 flex-wrap">
                                                    <div className="relative w-full">
                                                        <input
                                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            type="date"
                                                            value={formData?.quotes?.wall?.expectedDeliveryDate || ""}
                                                            onChange={(e) => handleNestedChange("quotes.wall.expectedDeliveryDate", e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        }

                                    </div>
                                )}

                                {/* Floor Specifications */}
                                {(formData.quotes.floor.isRequired) && (
                                    <div className="bg-green-50 p-4 rounded-lg space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800">Floor Specifications</h3>

                                        {/* Floor Count */}
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Number of Floors</p>
                                            <div className="flex gap-2 items-center">
                                                <input
                                                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    value={formData.jobInformation.floors.length}
                                                    onChange={(e) => {
                                                        const floorCount = parseInt(e.target.value) || 1;
                                                        const currentFloors = formData.jobInformation.floors;

                                                        if (floorCount > currentFloors.length) {
                                                            // Add new floors
                                                            const newFloors = [...currentFloors];
                                                            for (let i = currentFloors.length; i < floorCount; i++) {
                                                                newFloors.push({
                                                                    floorNumber: i + 1,
                                                                    floorTrussSize: "",
                                                                    flooring: "",
                                                                    balconyIncluded: false
                                                                });
                                                            }
                                                            handleNestedChange('jobInformation.floors', newFloors);
                                                        } else if (floorCount < currentFloors.length) {
                                                            // Remove floors
                                                            const newFloors = currentFloors.slice(0, floorCount);
                                                            handleNestedChange('jobInformation.floors', newFloors);
                                                        }
                                                    }}
                                                    placeholder="Floors"
                                                />
                                                <span className="text-sm text-gray-500">floors</span>
                                            </div>
                                        </div>

                                        {/* Individual Floor Settings */}
                                        {formData.jobInformation.floors.map((floor, index) => (
                                            <div key={index} className="bg-white p-4 rounded-lg border border-green-200 space-y-4">
                                                <h4 className="text-md font-semibold text-gray-800">
                                                    Floor {floor.floorNumber}
                                                </h4>

                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Floor Truss Size</p>
                                                    <div className="flex gap-2 sm:flex-row flex-col flex-wrap">
                                                        {["310", "370", "420", "Longreach"].map(size => (
                                                            <SelectionButton
                                                                key={size}
                                                                label={size}
                                                                isSelected={floor.floorTrussSize === size}
                                                                onClick={() => {
                                                                    const newFloors = [...formData.jobInformation.floors];
                                                                    newFloors[index].floorTrussSize = floor.floorTrussSize === size ? "" : size;
                                                                    handleNestedChange('jobInformation.floors', newFloors);
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>

                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 mb-2">Flooring Type</p>
                                                    <div className="flex gap-2 sm:flex-row flex-col">
                                                        <SelectionButton
                                                            label="Yellow tongue"
                                                            isSelected={floor.flooring === "Yellow tongue"}
                                                            onClick={() => {
                                                                const newFloors = [...formData.jobInformation.floors];
                                                                newFloors[index].flooring = floor.flooring === "Yellow tongue" ? "" : "Yellow tongue";
                                                                handleNestedChange('jobInformation.floors', newFloors);
                                                            }}
                                                        />
                                                        <SelectionButton
                                                            label="Compression sheet"
                                                            isSelected={floor.flooring === "Compression sheet"}
                                                            onClick={() => {
                                                                const newFloors = [...formData.jobInformation.floors];
                                                                newFloors[index].flooring = floor.flooring === "Compression sheet" ? "" : "Compression sheet";
                                                                handleNestedChange('jobInformation.floors', newFloors);
                                                            }}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <CheckboxButton
                                                        label="Balcony Included"
                                                        isChecked={floor.balconyIncluded}
                                                        onClick={() => {
                                                            const newFloors = [...formData.jobInformation.floors];
                                                            newFloors[index].balconyIncluded = !newFloors[index].balconyIncluded;
                                                            handleNestedChange('jobInformation.floors', newFloors);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        {/* Estimation Date Floor */}
                                        {/* <div>
                                            <p className="text-sm font-medium text-gray-700 mb-2">Expected Finish Date</p>
                                            <div className="flex gap-2 flex-wrap">
                                                <div className="relative w-full">
                                                    <input
                                                        className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        type="date"
                                                        name="estimationFinishDate"
                                                        value={formData?.quotes?.floor?.expectedFinishDate || ""}
                                                        onChange={(e) => handleNestedChange("quotes.floor.expectedFinishDate", e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Expected Delivery Date Floor */}
                                        {formData.jobType !== "Estimation" &&
                                            <div>
                                                <p className="text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</p>
                                                <div className="flex gap-2 flex-wrap">
                                                    <div className="relative w-full">
                                                        <input
                                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            type="date"
                                                            value={formData?.quotes?.floor?.expectedDeliveryDate || ""}
                                                            onChange={(e) => handleNestedChange("quotes.floor.expectedDeliveryDate", e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        }

                                    </div>
                                )}


                                {/* Site Measure */}
                                <div className="bg-yellow-50 p-4 rounded-lg space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-800">Site Measure</h3>

                                    <CheckboxButton
                                        label="Site Measure Required"
                                        isChecked={formData.jobInformation.siteMeasure.required}
                                        onClick={() => handleNestedChange('jobInformation.siteMeasure.required', !formData.jobInformation.siteMeasure.required)}
                                    />

                                    {formData.jobInformation.siteMeasure.required && (
                                        <div className="flex gap-2 sm:flex-row flex-col">
                                            {formData.quotes.wall.isRequired && (
                                                <CheckboxButton
                                                    label="Wall"
                                                    isChecked={formData.jobInformation.siteMeasure.wall}
                                                    onClick={() => handleNestedChange('jobInformation.siteMeasure.wall', !formData.jobInformation.siteMeasure.wall)}
                                                />
                                            )}
                                            {formData.quotes.floor.isRequired && (
                                                <CheckboxButton
                                                    label="Floor"
                                                    isChecked={formData.jobInformation.siteMeasure.floor}
                                                    onClick={() => handleNestedChange('jobInformation.siteMeasure.floor', !formData.jobInformation.siteMeasure.floor)}
                                                />
                                            )}
                                            {formData.quotes.roof.isRequired && (
                                                <CheckboxButton
                                                    label="Truss"
                                                    isChecked={formData.jobInformation.siteMeasure.truss}
                                                    onClick={() => handleNestedChange('jobInformation.siteMeasure.truss', !formData.jobInformation.siteMeasure.truss)}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Unit Information */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Additional Information</h2>

                        <div className="space-y-3">
                            <CheckboxButton
                                label="Is this a unit?"
                                isChecked={formData?.jobInformation?.isUnit}
                                onClick={() => handleNestedChange('jobInformation.isUnit', !formData?.jobInformation?.isUnit)}
                            />

                            {formData?.jobInformation?.isUnit && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Units</label>
                                    <input
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="number"
                                        value={formData.jobInformation.unitCount}
                                        onChange={(e) => handleNestedChange('jobInformation.unitCount', e.target.value)}
                                        placeholder="How many units?"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Priority Flags */}
                {!isTimberJob && (
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-orange-500 pb-2">Priority Settings</h2>
                        <div className="flex gap-3 sm:flex-row flex-col">
                            {formData?.jobType !== "Design" &&
                                <CheckboxButton
                                    label="Urgent Quote"
                                    isChecked={formData.jobInformation.urgentQuote}
                                    onClick={() => handleNestedChange('jobInformation.urgentQuote', !formData.jobInformation.urgentQuote)}
                                />
                            }
                            {formData?.jobType !== "Estimation" &&
                                <CheckboxButton
                                    label="Urgent Design"
                                    isChecked={formData.jobInformation.urgentDesign}
                                    onClick={() => handleNestedChange('jobInformation.urgentDesign', !formData.jobInformation.urgentDesign)}
                                />
                            }
                        </div>
                    </div>
                )}

                {/* Important Dates */}
                {formData.jobType !== "Design" &&
                    <div className="space-y-4">
                        <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Important Dates</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {!isTimberJob && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimation Finish Date</label>
                                    <div className="relative">
                                        <input
                                            className="w-full pl-9 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            type="date"
                                            name="estimationFinishDate"
                                            value={formData.estimationFinishDate}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                }

                {/* Notes */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-blue-500 pb-2">Notes</h2>

                    {/* Add New Note */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Add Note</label>
                        <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add any additional notes or requirements..."
                            rows="3"
                        />
                        {newNote.trim() && (
                            <button
                                type="button"
                                onClick={addNote}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                            >
                                Add Note
                            </button>
                        )}
                    </div>

                    {/* Existing Notes */}
                    {formData.jobInformation.notes.length > 0 && (
                        <div className="space-y-3">
                            <p className="text-sm font-medium text-gray-700">Previous Notes ({formData.jobInformation.notes.length}):</p>
                            {formData.jobInformation.notes.map((note, index) => (
                                <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                    {editingNoteIndex === index ? (
                                        // Edit Mode
                                        <div className="space-y-2">
                                            <textarea
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
                                                value={editingNoteContent}
                                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                                rows="3"
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => saveEditNote(index)}
                                                    className="px-3 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition-colors"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={cancelEditNote}
                                                    className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        // View Mode
                                        <>
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">{note.content}</p>
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs text-gray-500">
                                                    <span>By Admin • {formatDate(note.addedAt || new Date())}</span>
                                                    {note.isEdited && <span className="ml-2">• Edited</span>}
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => startEditNote(index, note.content)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                                        title="Edit note"
                                                    >
                                                        <FiEdit3 className="w-3 h-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteNote(index)}
                                                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                                        title="Delete note"
                                                    >
                                                        <FiX className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* File Upload */}
                <div className="space-y-4">
                    <h2 className="sm:text-xl text-lg font-semibold text-gray-800 border-b-2 border-purple-500 pb-2">Plans & Documents</h2>
                    <DragDropUploader
                        onFilesChange={handlePdfsChange}
                        clearFiles={clearPdfs}
                    />
                </div>


                {/* Quote Preview */}
                {formData?.jobType?.toLowerCase()?.includes("design") &&
                    <QuotePreview
                        uniqueId={formData.uniqueId}
                        quotes={formData.quotes}
                        jobInformation={formData.jobInformation}
                        isTimberJob={isTimberJob}
                    />
                }

                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                    <button
                        type="submit"
                        disabled={buttonLoading}
                        className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 focus:ring-4 focus:ring-blue-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {buttonLoading ? (
                            <div className="flex items-center justify-center gap-2">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Creating Job...
                            </div>
                        ) : (
                            "Create Construction Job"
                        )}
                    </button>
                </div>

                <ErrorDisplay errors={formErrors} />

            </form>

            {showDefaultDateConfirm && (
                <div className="mobile-dialog-shell fixed inset-0 top-0 !mt-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900">
                                Default Estimate Dates
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                This company has default estimate date enabled. These departments will use the dates below as estimated finish and delivery dates.
                            </p>
                        </div>

                        <div className="p-5 space-y-3">
                            {Object.entries(defaultDeptDates).map(([type, date]) => {
                                const cfg = DEPT_CONFIG[type];

                                return (
                                    <div
                                        key={type}
                                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3"
                                    >
                                        <div className="flex items-center gap-2 min-w-0">
                                            {cfg?.icon}
                                            <span className="text-sm font-semibold text-gray-800">
                                                {getQuoteLabel(type)}
                                            </span>
                                        </div>

                                        <input
                                            type="date"
                                            value={date}
                                            onChange={(e) => {
                                                setDefaultDeptDates(prev => ({
                                                    ...prev,
                                                    [type]: e.target.value,
                                                }));
                                            }}
                                            className="px-3 pl-8 py-2 border border-gray-300 rounded-lg text-sm"
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowDefaultDateConfirm(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                disabled={buttonLoading}
                                onClick={async () => {
                                    setShowDefaultDateConfirm(false);
                                    await submitJob(defaultDeptDates);
                                }}
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                            >
                                Continue & Create Job
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default NewAssignment;